/**
 * FuelTermsModal — Fuel-specific terms disclosure sub-modal.
 *
 * Opens from the "terms" link in the LoadFuel modal's fine print. The
 * generic /terms-of-use page doesn't cover Fuel-specific terms (price
 * fluctuation, 30-day withdrawal hold, non-refundable, on-chain pegging),
 * so this dialog spells them out explicitly inline with the purchase
 * flow. Pattern: rendered as a sub-modal layered above LoadFuelModal —
 * the Modal primitive's z-index counter handles stacking automatically.
 */

import React, {ReactElement} from "react";
import {FiX, FiAlertTriangle, FiTrendingUp, FiCreditCard, FiShoppingBag} from "react-icons/fi";
import {Modal} from "@/widgets/Modal";
import "./FuelTermsModal.scss";

interface Props {
    isOpen: boolean;
    toggle: () => void;
}

interface Section {
    icon: ReactElement;
    title: string;
    body: string;
}

/*
 * Featured sections — each gets an icon + framed treatment in the modal.
 * Ordered so the in-platform-balance positioning lands second, after the
 * fluctuation disclosure but before the standard payment terms.
 */
const SECTIONS: Section[] = [
    {
        icon: <FiTrendingUp aria-hidden="true" />,
        title: "Balance Value May Change",
        body: "The Fuel quantity credited at purchase is fixed. Its dollar-equivalent value can rise or fall over time, so the future USD value of your balance is not guaranteed.",
    },
    {
        icon: <FiShoppingBag aria-hidden="true" />,
        title: "In-Platform Balance",
        body: "Fuel is your DevourPlay balance — credit you can spend on Shop, Competition entries, and brand activations. Use it the same way you'd use any in-app currency.",
    },
    {
        icon: <FiAlertTriangle aria-hidden="true" />,
        title: "Non-Refundable",
        body: "Fuel purchases are final. Once a charge completes and Fuel is credited to your balance, the transaction cannot be reversed or refunded. Review your selection before confirming.",
    },
    {
        icon: <FiCreditCard aria-hidden="true" />,
        title: "Payment Authorization",
        body: "By confirming a purchase, you authorize the selected payment method to be charged the total amount shown. Your card issuer may require additional verification.",
    },
];

/*
 * Tail disclosure — withdrawal exists as an option but is intentionally
 * not featured. Rendered as plain text below the featured sections,
 * without an icon or panel, so it reads as a footnote rather than a
 * promoted capability. Pointer is `/account` (existing user account
 * page surfaces Fuel details) rather than "contact support" — we
 * don't want to encourage support reach-out.
 */
const WITHDRAWAL_NOTE = "Fuel is intended for in-platform use. Limited withdrawal options may be available for balances held longer than 30 days. See your account for details.";

function FuelTermsModal(props: Props): ReactElement {
    return (
        <Modal
            isOpen={props.isOpen}
            toggle={props.toggle}
            size="sm2"
            contentClassName="fuel-terms-modal"
            ariaLabel="Fuel Terms"
        >
            <header className="fuel-terms-modal__head">
                <span className="fuel-terms-modal__eyebrow">Disclosure</span>
                <button
                    type="button"
                    className="fuel-terms-modal__close"
                    onClick={props.toggle}
                    aria-label="Close"
                >
                    <FiX aria-hidden="true" />
                </button>
            </header>

            <h2 className="fuel-terms-modal__title">Fuel Terms</h2>
            <p className="fuel-terms-modal__sub">
                The terms below cover Fuel purchases specifically. They are in addition to the
                general <a href="/terms-of-use" target="_blank" rel="noreferrer">DevourPlay Terms of Use</a>.
            </p>

            <ul className="fuel-terms-modal__list">
                {SECTIONS.map(s =>
                    <li key={s.title} className="fuel-terms-modal__item">
                        <span className="fuel-terms-modal__item-icon">{s.icon}</span>
                        <div className="fuel-terms-modal__item-body">
                            <h3 className="fuel-terms-modal__item-title">{s.title}</h3>
                            <p className="fuel-terms-modal__item-copy">{s.body}</p>
                        </div>
                    </li>)}
            </ul>

            <p className="fuel-terms-modal__tail">
                {WITHDRAWAL_NOTE}
            </p>

            <div className="fuel-terms-modal__cta-row">
                <button
                    type="button"
                    className="fuel-terms-modal__close-cta"
                    onClick={props.toggle}
                >
                    I Understand
                </button>
            </div>
        </Modal>
    );
}

export default FuelTermsModal;
